/**
 * Billing402Gate — the shared app-level 402 handler (Plan 02 D3, the egress /
 * `local`-mode row): mounted once around an application, it watches every
 * fetch the app makes and, when the hub plane answers **402** (a metered
 * threshold crossed — egress today, any service tomorrow), opens the
 * checkout sheet for the plan the server's own denial suggests.
 *
 * Scope discipline:
 *   - only the hub's quota envelopes react (`QUOTA_EXCEEDED`,
 *     `ENTITLEMENT_DENIED` — the contract-locked 402 bodies). A payment
 *     decline's 402 (`PAYMENT_DECLINED`) belongs to the sheet's own failure
 *     state and never re-triggers the gate.
 *   - the plan comes from the denial's `details`: `suggestedPlanId` first,
 *     else the service's cheapest one-time pack. The UI never invents a
 *     product — with nothing resolvable the gate stays quiet and the
 *     settings row's overage banner remains the display surface.
 *   - one sheet per overage episode: closing it dismisses the service until
 *     a checkout succeeds (the episode's only resolution), so a polling loop
 *     against a 402-ing endpoint cannot trap the user in dialogs.
 *
 * The watcher wraps `window.fetch` for the gate's lifetime and restores the
 * original on unmount; responses always pass through untouched.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { billingClient } from "../client/billingClient";
import { invalidateBillingCache } from "../hooks/billingCache";
import { BillingLabelsProvider, type BillingLabels } from "../strings";
import type { Plan } from "../types";
import { UpgradeSheet } from "./UpgradeSheet";

export interface Billing402GateProps {
  children: ReactNode;
  /** The application's i18n seam for the sheet's strings (English defaults
   *  when omitted). */
  labels?: Partial<BillingLabels>;
}

/** The 402 error codes the hub plane produces for a crossed threshold. */
const QUOTA_CODES = new Set(["QUOTA_EXCEEDED", "ENTITLEMENT_DENIED"]);

/** The upgrade deep link inside a denial's details (`ortam://upgrade/<id>`). */
const UPGRADE_URL_PREFIX = "ortam://upgrade/";

interface Quota402 {
  serviceId: string;
  suggestedPlanId: string | null;
}

/** Read the hub's quota envelope off a 402 response; null for any other body. */
async function readQuota402(response: Response): Promise<Quota402 | null> {
  try {
    const body = (await response.json()) as {
      error?: { code?: string; details?: { upgradeUrl?: string; suggestedPlanId?: string } };
    };
    const code = body.error?.code;
    if (!code || !QUOTA_CODES.has(code)) return null;
    const details = body.error?.details ?? {};
    const upgradeUrl = details.upgradeUrl ?? "";
    const serviceId = upgradeUrl.startsWith(UPGRADE_URL_PREFIX) ? upgradeUrl.slice(UPGRADE_URL_PREFIX.length) : null;
    if (!serviceId) return null;
    return { serviceId, suggestedPlanId: details.suggestedPlanId ?? null };
  } catch {
    // A non-JSON 402 is not the hub's quota envelope — ignore it.
    return null;
  }
}

/** The cheapest one-time pack the catalog sells for a service, if any. */
function cheapestPack(plans: Plan[], serviceId: string): Plan | null {
  const packs = plans
    .filter((plan) => plan.serviceId === serviceId && plan.billingCycle === "one_time")
    .sort((a, b) => Number.parseFloat(a.prices.usd ?? "0") - Number.parseFloat(b.prices.usd ?? "0"));
  return packs[0] ?? null;
}

function GateInner({ children }: { children: ReactNode }) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const planRef = useRef<Plan | null>(null);
  planRef.current = plan;
  // Services the user dismissed this episode — cleared when a checkout lands.
  const dismissedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const original = window.fetch.bind(window);

    const handle = async (response: Response) => {
      const quota = await readQuota402(response);
      if (!quota) return;
      if (dismissedRef.current.has(quota.serviceId)) return;
      if (planRef.current?.serviceId === quota.serviceId) return;
      try {
        const answer = await billingClient.getPlans();
        const suggested = quota.suggestedPlanId
          ? answer.plans.find((candidate) => candidate.id === quota.suggestedPlanId) ?? null
          : null;
        const resolved = suggested ?? cheapestPack(answer.plans, quota.serviceId);
        if (resolved) setPlan(resolved);
        else dismissedRef.current.add(quota.serviceId);
      } catch {
        // No catalog, no affordance — the settings row stays the surface.
      }
    };

    window.fetch = async (input, init) => {
      const response = await original(input, init);
      if (response.status === 402) void handle(response.clone());
      return response;
    };
    return () => {
      window.fetch = original;
    };
  }, []);

  const close = useCallback(() => {
    if (plan) dismissedRef.current.add(plan.serviceId);
    setPlan(null);
  }, [plan]);

  return (
    <>
      {children}
      <UpgradeSheet
        open={plan !== null}
        plan={plan}
        onClose={close}
        onSuccess={() => {
          // The overage episode resolved: every service may gate again.
          dismissedRef.current.clear();
          invalidateBillingCache();
          setPlan(null);
        }}
      />
    </>
  );
}

export function Billing402Gate({ children, labels }: Billing402GateProps) {
  const gate = <GateInner>{children}</GateInner>;
  return labels ? <BillingLabelsProvider labels={labels}>{gate}</BillingLabelsProvider> : gate;
}
